import React, { useRef, useContext, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { Select } from "antd";
import { Download, Telegram, X, Share } from "@mui/icons-material";
import html2canvas from "html2canvas";
import PnlChart from "./PnlChart";
import AppContext from "../../context/Context";
import { XIcon } from "lucide-react";

const PnlMtmModal = ({
  show,
  onHide,
  title = "PNL / MTM Graph",
  defaultLevel = "broker",
  brokerId,
  brokerName,
  strategyId,
  baskets = [], 
  positions = [],
  selection = true,
}) => {
  const { config: { isDark } } = useContext(AppContext);
  const modalBodyRef = useRef(null);
  const [level, setLevel] = useState(defaultLevel);
  const [selectedBasket, setSelectedBasket] = useState(baskets?.[0]?.id);
  const [selectedPosition, setSelectedPosition] = useState(positions?.[0]?.id);

  const displayBrokerName = brokerName
    ? `${String(brokerName).charAt(0).toUpperCase()}${String(brokerName).slice(1)}`
    : null;

  const LEVELS = [
    { key: "position", label: "Position" },
    { key: "basket", label: "Strategy" },
    ...(defaultLevel === 'broker'
      ? [{ key: "broker", label: displayBrokerName ? `Account (${displayBrokerName})` : "Account" }]
      : [{ key: "algo-broker", label: displayBrokerName ? `Account (${displayBrokerName})` : "Account" }]
    )
  ];


  const formatMoney = (n) => {
    const val = Number(n || 0);
    return `${val < 0 ? "-" : ""}₹${Math.abs(val).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  const formatPct = (n) => `${Number(n || 0).toFixed(2)}%`;

  const formatTime = (unix) => {
    if (!unix) return "-";
    const d = new Date(unix * 1000);
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  const captureModalBody = async () => {
    if (!modalBodyRef.current) return null;
    
    try {
      const canvas = await html2canvas(modalBodyRef.current, {
        backgroundColor: isDark ? "#0f172a" : "#ffffff",
        scale: 2, 
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: modalBodyRef.current.scrollWidth,
        height: modalBodyRef.current.scrollHeight,
      });
      return canvas.toDataURL("image/png");
    } catch (error) {
      console.error("Error capturing modal body:", error);
      return null;
    }
  };

  const shareText = () => {
    const lines = [
      `${title}`,
      `Total PnL: ${formatMoney(0)}`,
      `Max Drawdown: ${formatMoney(0)} (${formatPct(0)})`,
      `Period: ${formatTime(null)} → ${formatTime(null)}`,
    ];
    return lines.join("\n");
  };

  const downloadImage = async () => {
    const dataUrl = await captureModalBody();
    if (!dataUrl) return;
    
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `pnl_mtm_summary_${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  
  const shareTwitter = async () => {
    const imageData = await captureModalBody();
    if (!imageData) return;
    
    const response = await fetch(imageData);
    const blob = await response.blob();
    const file = new File([blob], `pnl_summary_${Date.now()}.png`, { type: 'image/png' });
    
    if (navigator.share && navigator.canShare({ files: [file] })) {
      navigator.share({
        title: title,
        text: shareText(),
        files: [file]
      });
    } else {
      const text = encodeURIComponent(shareText() + " #PnLChart");
      const url = `https://twitter.com/intent/tweet?text=${text}`;
      window.open(url, "_blank");
    }
  };
  
  const shareTelegram = async () => {
    const imageData = await captureModalBody();
    if (!imageData) return;
    
    const response = await fetch(imageData);
    const blob = await response.blob();
    const file = new File([blob], `pnl_summary_${Date.now()}.png`, { type: 'image/png' });
    
    if (navigator.share && navigator.canShare({ files: [file] })) {
      navigator.share({
        title: title,
        text: shareText(),
        files: [file]
      });
    } else {
      const a = document.createElement("a");
      a.href = imageData;
      a.download = `pnl_summary_${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      
      if (navigator.clipboard) {
        navigator.clipboard.writeText(shareText());
        alert("Summary image downloaded and text copied to clipboard. Please share manually in Telegram.");
      } else {
        alert("Summary image downloaded. Please share manually in Telegram.");
      }
    }
  };

  const shareNative = async () => {
    const imageData = await captureModalBody();
    if (!imageData) return;
    
    try {
      const response = await fetch(imageData);
      const blob = await response.blob();
      const file = new File([blob], `pnl_summary_${Date.now()}.png`, { type: 'image/png' });
      if (navigator.share) {
        const shareData = {
          title: title,
          text: shareText(),
        };
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          shareData.files = [file];
        }
        await navigator.share(shareData);
      } else {
        alert("Native sharing is not supported on this browser.");
      }
    } catch (e) {
      console.error('Native share failed:', e);
    }
  };
  
  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header>
        <Modal.Title style={{minWidth: "150px"}}>{title}</Modal.Title>
        <div className="d-flex align-items-center ms-3" style={{justifyContent: "space-between", width: "100%"}} >
            <div className="d-flex align-items-center gap-2" >
          {selection && (
            <>
            <Select
              value={level}
              style={{ minWidth: 140 }}
              onChange={(val)=> setLevel(val)}
              getPopupContainer={(trigger)=> trigger.parentNode}
              dropdownStyle={{ zIndex: 2000 }}
              options={LEVELS.map((l)=>({ label: l.label, value: l.key, disabled: !( (l.key === 'broker' && brokerId) || (l.key === 'algo-broker' && brokerId) || (l.key === 'basket' && baskets.length) || (l.key === 'position' && positions.length) ) }))}
            />
            {level === 'basket' && (
              <Select
                value={selectedBasket}
                style={{ minWidth: 200 }}
                onChange={(val)=> setSelectedBasket(val)}
                getPopupContainer={(trigger)=> trigger.parentNode}
                dropdownStyle={{ zIndex: 2000 }}
                options={baskets.map((b)=>({ label: b.name || b.id, value: b.id }))}
              />
            )}
            {level === 'position' && (
              <Select
                value={selectedPosition}
                style={{ minWidth: 240 }}
                onChange={(val)=> setSelectedPosition(val)}
                getPopupContainer={(trigger)=> trigger.parentNode}
                dropdownStyle={{ zIndex: 2000 }}
                options={positions.map((p)=>({ label: p.name || p.id, value: p.id }))}
                />
              )}
            </>
          )}
            </div>
          <div className="d-flex align-items-center ms-2 gap-2" >
          <Button size="sm" style={{backgroundColor: "#1a2639", border: "none", padding:"4px" }} onClick={downloadImage}><Download fontSize="small" /></Button>
          <Button size="sm" style={{backgroundColor: "#000000", border: "none", padding:"4px" }} onClick={shareTwitter}><X fontSize="small" /></Button>
          <Button size="sm" style={{backgroundColor: "#0088cc", border: "none", padding:"4px" }} onClick={shareTelegram}><Telegram fontSize="small" /></Button>
          <Button size="sm" style={{ backgroundColor: isDark ? "#334155" : "#e5e7eb", border: "none", padding: "4px" }} onClick={shareNative}>
            <Share fontSize="small" style={{ color: isDark ? "#e2e8f0" : "#111827" }} />
          </Button>
          </div>
        </div>
        <button
            type="button"
            className="modal-close-button ms-4"
            onClick={() => onHide()}
            aria-label="Close"
          >
            <XIcon />
          </button>
      </Modal.Header>
      <Modal.Body ref={modalBodyRef}>
        <PnlChart
          brokerId={brokerId}
          brokerName={brokerName}
          strategyId={strategyId}
          baskets={baskets}
          positions={positions}
          showSummary={true}
          height={400}
          level={level}
          selectedBasket={selectedBasket}
          selectedPosition={selectedPosition}
        />
      </Modal.Body>
    </Modal>
  );
};

export default PnlMtmModal;